'use client';

import { c as _c } from "react/compiler-runtime";
import Link from '@/components/ui/Link';
import { Section, Row, Column, PageComponent } from '@/lib/types/pageBuilder';
import { ArticleTemplateConfig } from '@/lib/local-store/types';
import { MultiComponentRenderer } from '@/components/page-builder/ComponentRenderer';
import TaboolaLoader from '@/components/ads/TaboolaLoader';
import TaboolaRightRail from '@/components/ads/TaboolaRightRail';
import TaboolaInfiniteScrollWidgets from '@/components/ads/TaboolaInfiniteScrollWidgets';
import VuukleComments from '@/components/ads/VuukleComments';
import OnesideText from '@/components/component_astro_crick/OnesideText';
import HorizontalSmallImageCategoryNews from '@/components/component_horizontal/HorizontalSmallImageCategoryNews';
import HorizontalSmallVideoRightNews from '@/components/component_horizontal/HorizontalSmallVideoRightNews';
import ArticleHeroSection from './ArticleHeroSection';
import BreadcrumbTrail from './BreadcrumbTrail';
import AuthorTrail from './AuthorTrail';
import ArticleTextBody from './templates/ArticleTextBody';
import ArticleListicleBody from './templates/ArticleListicleBody';
import ArticleQABody from './templates/ArticleQABody';
import ArticlePhotoBody from './templates/ArticlePhotoBody';
import ArticleInterviewBody from './templates/ArticleInterviewBody';
import ArticleVideoBody from './templates/ArticleVideoBody';
import ArticleGumletVideoBody from './templates/ArticleGumletVideoBody';
import ArticleLiveBlogBody from './templates/ArticleLiveBlogBody';
import InArticleAdRenderer from './InArticleAdRenderer';
import { InArticleAdSlot } from '@/lib/types/articleTemplate';
import { buildParagraphAdPlan, computeParagraphMap, getAdSlotForParagraph, shouldShowAdAfterParagraph } from '@/lib/articleInlineAds';
import { getImageUrl, formatTimeAgo } from '@/lib/api';
import { FALLBACK_IMAGE } from '@/lib/constants';
export interface ArticleTemplateRendererProps {
  story: any;
  template: ArticleTemplateConfig;
  /** website-settings 'show-article-published-on' — resolved server-side by the caller. */
  showPublishedOn?: boolean;
  /** Related stories for this article (already fetched server-side, e.g. perArticleRelated[0]). */
  relatedStories?: any[];
  /** Site-wide recent videos (already fetched server-side), for the sidebar videos section. */
  videos?: any[];
}
interface RenderContext {
  story: any;
  showPublishedOn: boolean;
  relatedStories: any[];
  videos: any[];
  /** template.templateType — which dedicated body component to use is keyed
   *  off this, NOT off the body-marker's component_type. Builder-authored
   *  templates don't reliably use the "correct" marker (e.g. a live synced
   *  listicle/question-and-answer template both use the generic
   *  ArticleTextTemplate marker), so templateType is the only reliable signal. */
  templateType: string;
}

// The 11 template-marker component types (docs/ARTICLE_TEMPLATES_AND_COMPONENTS_REFERENCE.md
// §1/§2) are structural markers with the same Visibility/Sections/In-Article-Ads
// props, carrying no template-specific layout of their own — but the legacy
// per-story-template renderer (ArticleListInfinite.tsx) that this dynamic
// renderer mirrors DOES give several template types their own hand-styled body
// (listicle's numbered-circle badges, Q&A's highlighted question cards, review's
// star rating, video/photo/interview's dedicated layouts...). Most markers still
// route to the generic ArticleTextBody (verbatim from the text/syndicated
// branch), but listicle and question-and-answer have dedicated bodies — see the
// `templateType === 'listicle' / 'question-and-answer'` branches in
// renderArticleComponent below. Dispatch is keyed off RenderContext.templateType
// (the template's own templateType), NOT off this marker's component_type —
// live synced templates show a Builder author can drop the generic
// ArticleTextTemplate marker into a listicle/Q&A template and it still needs
// to render with that template's dedicated body. Extend those branches as more
// template-specific bodies get extracted from the legacy per-template branches.
const TEMPLATE_BODY_MARKER_TYPES = new Set(['ArticleTextTemplate', 'ArticleListicleTemplate', 'ArticleVideoTemplate', 'ArticleQuestionAnswerTemplate', 'ArticleGumletVideoTemplate', 'ArticleReviewTemplate', 'ArticleSyndicatedTemplate', 'ArticlePhotoTemplate', 'ArticleInterviewTemplate', 'ArticlePhotoVisualTemplate', 'ArticleLiveBlogTemplate']);
const ARTICLE_COMPONENT_TYPES = new Set(['ArticleHeroSection', 'ArticleBreadcrumb', 'ArticleAuthorBlock', 'ArticleComments', 'ArticleTaboolaRightRail', 'ArticleTaboolaBottom', 'ArticleRelatedStories', ...TEMPLATE_BODY_MARKER_TYPES]);

// story-templates that hide the sidebar videos section — mirrors the legacy
// ArticleListInfinite.tsx sidebar exactly (a video/photo article doesn't need
// a "more videos" rail directly under its own hero media).
const HIDE_SIDEBAR_VIDEOS_FOR = new Set(['video', 'gumlet-video', 'photo', 'photo-visual']);
function buildInlineAdRenderer(adSlots: InArticleAdSlot[] | undefined, story: any) {
  const hasParagraphs = Object.keys(computeParagraphMap(story)).length > 0;
  const plan = buildParagraphAdPlan(adSlots || [], hasParagraphs);
  return (globalIdx: number) => {
    if (!shouldShowAdAfterParagraph(plan, globalIdx)) return null;
    const adSlot = getAdSlotForParagraph(plan, globalIdx);
    if (!adSlot) return null;
    return <InArticleAdRenderer slot={adSlot} adIndex={globalIdx} />;
  };
}

/**
 * "தொடர்புடையது" (Related) list + "விடியோக்கள்" (Videos) list — mirrors the
 * legacy right-sidebar content in ArticleListInfinite.tsx (minus the
 * AdSlotConfig-backed "big-ad-1" box, which this dynamic path intentionally
 * doesn't use). Bound to the ArticleRelatedStories marker slot in the
 * template, using data the caller already fetched server-side.
 */
function RelatedStoriesSidebar(t0) {
  const $ = _c(14);
  const {
    story,
    relatedStories,
    videos
  } = t0;
  const t1 = story.sections?.[0]?.name || "";
  let t2;
  if ($[0] !== t1) {
    t2 = t1.trim();
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const category = t2;
  let t3;
  if ($[2] !== story || $[3] !== videos.length) {
    t3 = videos.length > 0 && !HIDE_SIDEBAR_VIDEOS_FOR.has(story["story-template"]);
    $[2] = story;
    $[3] = videos.length;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const showVideos = t3;
  let t4;
  if ($[5] !== category || $[6] !== relatedStories) {
    t4 = relatedStories.length > 0 && <div className="border-b border-DCDCDC relative z-[1]"><div className="border-b border-DCDCDC"><OnesideText text={"\u0BA4\u0BCA\u0B9F\u0BB0\u0BCD\u0BAA\u0BC1\u0B9F\u0BC8\u0BAF\u0BA4\u0BC1"} /></div>{relatedStories.map((relatedStory, rIndex) => {
        const card = <HorizontalSmallImageCategoryNews category={{
          value: category
        }} title={{
          value: relatedStory.headline
        }} timeAgo={{
          value: formatTimeAgo(relatedStory["last-published-at"])
        }} imageSrc={{
          value: relatedStory["hero-image-s3-key"] ? getImageUrl(relatedStory["hero-image-s3-key"]) : FALLBACK_IMAGE
        }} imageMetadata={relatedStory["hero-image-metadata"]} templatetype={{
          value: relatedStory["story-template"]
        }} />;
        return relatedStory.slug ? <Link key={`${relatedStory.slug}-${rIndex}`} href={`/${relatedStory.slug}`} className="block transition-colors cursor-pointer">{card}</Link> : <div key={`related-${rIndex}`}>{card}</div>;
      })}</div>;
    $[5] = category;
    $[6] = relatedStories;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== showVideos || $[9] !== videos) {
    t5 = showVideos && <><div className="border-b border-DCDCDC"><OnesideText text={"\u0BB5\u0BBF\u0B9F\u0BBF\u0BAF\u0BCB\u0B95\u0BCD\u0B95\u0BB3\u0BCD"} /></div><div className="border-b border-DCDCDC"><HorizontalSmallVideoRightNews videos={videos.slice(0, 2).map(_temp)} /></div></>;
    $[8] = showVideos;
    $[9] = videos;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function _temp(video) {
  return {
    title: {
      hidden: false,
      value: video.headline || "",
      customClass: ""
    },
    slug: video.slug,
    imageSrc: {
      hidden: false,
      value: video["hero-image-s3-key"] || "",
      customClass: ""
    },
    timeAgo: {
      hidden: false,
      value: video["content-created-at"] || "",
      customClass: ""
    },
    authorName: video["author-name"] || ""
  };
}
function renderArticleComponent(component: PageComponent, ctx: RenderContext): React.ReactNode {
  const {
    story,
    showPublishedOn,
    relatedStories,
    videos,
    templateType
  } = ctx;
  const props = (component.props || {}) as Record<string, any>;
  if (component.component_type && TEMPLATE_BODY_MARKER_TYPES.has(component.component_type)) {
    const category = (story.sections?.[0]?.name || '').trim();
    const sectionUrl = story.sections?.[0]?.['section-url'] ? story.sections[0]['section-url'].replace(/^https?:\/\/(www\.)?dinamani\.com\/?/, '/') : '';
    const imageUrl = story['hero-image-s3-key'] ? getImageUrl(story['hero-image-s3-key']) : '';
    const updatedTimestamp = story['updated-at'] || story['content-updated-at'] || story['last-published-at'] || story['published-at'];
    const publishedAt = formatTimeAgo(story['published-at']);
    const updatedAt = formatTimeAgo(updatedTimestamp);
    const renderInlineAd = buildInlineAdRenderer(props.inArticleAds, story);
    if (templateType === 'listicle' || templateType === 'review') {
      return <ArticleListicleBody story={story} index={0} activeAdArticleIndex={0} category={category} sectionUrl={sectionUrl} imageUrl={imageUrl} imageAttribution={story['hero-image-attribution'] || ''} publishedAt={publishedAt} updatedAt={updatedAt} showPublishedOn={showPublishedOn} showAudioPlayer={false} promotionBlock={null} renderInlineAd={renderInlineAd} showRelatedStory={props.showRelatedStory !== false} showLinkedStories={props.showLinkedStories !== false} showStoryTags={props.showStoryTags !== false} showComments={false} showHero={false} variant={templateType === 'review' ? 'review' : 'listicle'} />;
    }
    if (templateType === 'question-and-answer') {
      return <ArticleQABody story={story} index={0} activeAdArticleIndex={0} category={category} imageUrl={imageUrl} publishedAt={publishedAt} updatedAt={updatedAt} showAudioPlayer={false} promotionBlock={null} renderInlineAd={renderInlineAd} showRelatedStory={props.showRelatedStory !== false} showPromotionalMessage={props.showPromotionalMessage !== false} showLinkedStories={props.showLinkedStories !== false} showStoryTags={props.showStoryTags !== false} showComments={false} showHero={false} />;
    }
    if (templateType === 'photo') {
      return <ArticlePhotoBody story={story} index={0} activeAdArticleIndex={0} category={category} sectionUrl={sectionUrl} imageUrl={imageUrl} updatedAt={updatedAt} showAudioPlayer={false} promotionBlock={null} renderInlineAd={renderInlineAd} showRelatedStory={props.showRelatedStory !== false} showLinkedStories={props.showLinkedStories !== false} showStoryTags={props.showStoryTags !== false} showComments={false} showHero={false} />;
    }
    if (templateType === 'interview') {
      return <ArticleInterviewBody story={story} index={0} activeAdArticleIndex={0} category={category} imageUrl={imageUrl} updatedAt={updatedAt} showAudioPlayer={false} promotionBlock={null} renderInlineAd={renderInlineAd} showRelatedStory={props.showRelatedStory !== false} showPromotionalMessage={props.showPromotionalMessage !== false} showLinkedStories={props.showLinkedStories !== false} showStoryTags={props.showStoryTags !== false} showComments={false} showHero={false} />;
    }
    if (templateType === 'video') {
      return <ArticleVideoBody story={story} index={0} activeAdArticleIndex={0} category={category} sectionUrl={sectionUrl} imageUrl={imageUrl} imageAttribution={story['hero-image-attribution'] || ''} publishedAt={publishedAt} updatedAt={updatedAt} showPublishedOn={showPublishedOn} showAudioPlayer={false} promotionBlock={null} renderInlineAd={renderInlineAd} showRelatedStory={props.showRelatedStory !== false} showLinkedStories={props.showLinkedStories !== false} showStoryTags={props.showStoryTags !== false} showComments={false} showHero={false} />;
    }
    if (templateType === 'gumlet-video') {
      return <ArticleGumletVideoBody story={story} index={0} activeAdArticleIndex={0} category={category} imageUrl={imageUrl} updatedAt={updatedAt} showAudioPlayer={false} promotionBlock={null} renderInlineAd={renderInlineAd} showRelatedStory={props.showRelatedStory !== false} showPromotionalMessage={props.showPromotionalMessage !== false} showLinkedStories={props.showLinkedStories !== false} showStoryTags={props.showStoryTags !== false} showComments={false} showHero={false} />;
    }
    if (templateType === 'live-blog') {
      return <ArticleLiveBlogBody story={story} index={0} activeAdArticleIndex={0} category={category} sectionUrl={sectionUrl} imageUrl={imageUrl} imageAttribution={story['hero-image-attribution'] || ''} updatedAt={updatedAt} showAudioPlayer={false} promotionBlock={null} renderInlineAd={renderInlineAd} showRelatedStory={props.showRelatedStory !== false} showLinkedStories={props.showLinkedStories !== false} showStoryTags={props.showStoryTags !== false} showComments={false} showHero={false} />;
    }
    return <ArticleTextBody story={story} index={0} activeAdArticleIndex={0} category={category} sectionUrl={sectionUrl} imageUrl={imageUrl} imageAttribution={story['hero-image-attribution'] || ''} publishedAt={publishedAt} updatedAt={updatedAt} showPublishedOn={showPublishedOn} showAudioPlayer={false} promotionBlock={null} renderInlineAd={renderInlineAd} showSummary={props.showSummary !== false} showRelatedStory={props.showRelatedStory !== false} showPromotionalMessage={props.showPromotionalMessage !== false} showLinkedStories={props.showLinkedStories !== false} showStoryTags={props.showStoryTags !== false} showComments={false} showHero={false} />;
  }
  switch (component.component_type) {
    case 'ArticleHeroSection':
      return <ArticleHeroSection heroFields={props.heroFields || []} story={story} templateType={templateType} />;
    case 'ArticleBreadcrumb':
      return <BreadcrumbTrail items={props.breadcrumbItems} story={story} />;
    case 'ArticleAuthorBlock':
      return <AuthorTrail items={props.authorItems} story={story} />;
    case 'ArticleComments':
      return <VuukleComments articleId={story.id} articleTitle={story.headline || ''} articleUrl={`https://www.dinamani.com/${story.slug}`} articleImage={story['hero-image-s3-key'] ? getImageUrl(story['hero-image-s3-key']) : ''} articleTags={story.tags?.slice(0, 3).map((t: any) => t.name).join(',') || story.sections?.[0]?.name || ''} articleAuthor={story.authors?.[0]?.name || 'Dinamani'} disclaimer={props.disclaimer} />;
    case 'ArticleTaboolaRightRail':
      {
        // key={story.slug}: these components push to Taboola exactly once per
        // component INSTANCE (a `pushedRef` guard, not a prop-based check) —
        // correct for the legacy infinite-scroll list where each scrolled-in
        // article gets a fresh index-keyed instance, but on client-side
        // navigation between two articles here there's no natural prop change
        // forcing a remount (component.id comes from the template definition,
        // so it's identical across every article using this template). Without
        // this key, React reuses the same instance on navigation, the guard is
        // already tripped, and Taboola never learns about the new article's
        // URL — it only "worked" because a full page refresh remounts fresh.
        //
        // pageIndex must ALSO be unique per article, not hardcoded 0: it's
        // used to build the widget's container id
        // (taboola-*-thumbnails-${pageIndex}), and Taboola de-dupes/ignores a
        // push into a container id it has already seen — even across a fresh
        // React remount, the id string itself is what Taboola tracks. With
        // pageIndex always 0, every dynamically-rendered article reused the
        // exact same container id as the previous one, so the widget populated
        // on the first article a reader landed on and silently stayed empty on
        // every article navigated to after that. story.id is a stable,
        // per-story id (this Quintype instance uses UUID strings, not legacy
        // numeric ids — pageIndex accepts either), so using it keeps every
        // article's container id genuinely unique.
        // lg:sticky lg:top-10 matches the legacy sidebar's Taboola right-rail
        // exactly (ArticleListInfinite.tsx) — desktop-only, so it stays in view
        // as the reader scrolls the (usually taller) main article column.
        const pageIndex = story.id ?? story.slug ?? 0;
        return <div className="lg:sticky lg:top-10">
          <TaboolaRightRail key={story.slug} pageIndex={pageIndex} canonicalUrl={`https://www.dinamani.com/${story.slug}`} />
        </div>;
      }
    case 'ArticleTaboolaBottom':
      {
        // See the pageIndex comment on ArticleTaboolaRightRail above — same fix.
        const pageIndex = story.id ?? story.slug ?? 0;
        return <TaboolaInfiniteScrollWidgets key={story.slug} pageIndex={pageIndex} canonicalUrl={`https://www.dinamani.com/${story.slug}`} />;
      }
    case 'ArticleRelatedStories':
      return <RelatedStoriesSidebar story={story} relatedStories={relatedStories} videos={videos} />;
    default:
      return null;
  }
}
function renderComponent(component: PageComponent, ctx: RenderContext): React.ReactNode {
  if (component.component_type && ARTICLE_COMPONENT_TYPES.has(component.component_type)) {
    // flex-1: ArticleTaboolaRightRail's lg:sticky wrapper (see that case in
    // renderArticleComponent) needs its parent to actually grow to fill the
    // stretched grid column's height, or it has no room to stick — a plain
    // shrink-to-fit wrapper here (the default, correct for every other
    // component type) breaks position:sticky's containing block entirely.
    // Confirmed via isolated repro: same grid/flex nesting, sticky works iff
    // this wrapper has flex-1, regardless of top/position being computed
    // correctly on the sticky element itself.
    const className = component.component_type === 'ArticleTaboolaRightRail' ? 'flex-1' : undefined;
    return <div key={component.id} className={className}>{renderArticleComponent(component, ctx)}</div>;
  }
  // Fall back to the generic page-builder component renderer for any
  // non-Article component type a template author places in the tree
  // (plain HtmlWidget, image, iframe, etc.) — no item-data binding available
  // here (article templates aren't collection-bound), so items is empty.
  return <div key={component.id}>
      <MultiComponentRenderer components={[component]} items={[]} />
    </div>;
}

// Tailwind class maps for grid-column spans — mirrors ColumnContentRenderer.tsx/
// RowRenderer.tsx exactly (col-span-N only works inside a `grid`, not `flex`,
// and must be a literal string for Tailwind's static class scanner to pick up).
const colSpanMap: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12'
};
const mdColSpanMap: Record<number, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12'
};
const lgColSpanMap: Record<number, string> = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
  7: 'lg:col-span-7',
  8: 'lg:col-span-8',
  9: 'lg:col-span-9',
  10: 'lg:col-span-10',
  11: 'lg:col-span-11',
  12: 'lg:col-span-12'
};
function getWidthClass(width: Column['width'] | undefined): string {
  const mobileClass = colSpanMap[width?.mobile ?? 12] || 'col-span-12';
  const tabletClass = mdColSpanMap[width?.tablet ?? 12] || 'md:col-span-12';
  const desktopClass = lgColSpanMap[width?.desktop ?? 12] || 'lg:col-span-12';
  return `${mobileClass} ${tabletClass} ${desktopClass}`;
}

/**
 * Renders a column's children in `children_order` (interleaving components,
 * nested-columns, and nested-rows in the template author's intended order —
 * NOT `components` array order, which drops nested content to the end and
 * loses relative position, e.g. a Taboola component meant to sit after the
 * body ending up before it). Falls back to components-then-columns-then-rows
 * only when no children_order is present (e.g. a manually hand-built template).
 */
function renderColumnChildren(column: Column, ctx: RenderContext): React.ReactNode[] {
  if (column.children_order && column.children_order.length > 0) {
    return column.children_order.map(child => {
      if (child.type === 'component') {
        const component = column.components?.find(c => c.id === child.id);
        return component ? renderComponent(component, ctx) : null;
      }
      if (child.type === 'nested-column') {
        const nestedColumn = column.columns?.find(c => c.id === child.id);
        return nestedColumn ? renderColumn(nestedColumn, ctx) : null;
      }
      if (child.type === 'nested-row') {
        const nestedRow = column.rows?.find(r => r.id === child.id);
        return nestedRow ? renderRow(nestedRow, ctx) : null;
      }
      return null;
    });
  }
  return [...(column.components || []).map(component => renderComponent(component, ctx)), ...(column.columns || []).map(nested => renderColumn(nested, ctx)), ...(column.rows || []).map(nested => renderRow(nested, ctx))];
}
function renderColumn(column: Column, ctx: RenderContext, showRightDivider = false): React.ReactNode {
  // lg:border-r only (not on mobile/tablet, where columns stack full-width
  // instead of sitting side by side — a right border there would just cut
  // across the middle of the stacked content, not separate two columns).
  // border-DCDCDC is NOT a generated Tailwind utility here — it's a
  // hand-written, non-responsive rule in globals.css (`.border-DCDCDC {
  // border-color: ... }`), so an `lg:border-DCDCDC` variant doesn't exist
  // and silently matches nothing (border-color then falls back to
  // currentColor/black). Applying the unprefixed class is safe regardless
  // of breakpoint — it only sets border-COLOR, and the border stays
  // invisible (0 width) below lg where `lg:border-r` hasn't kicked in.
  const dividerClass = showRightDivider ? 'lg:border-r border-DCDCDC' : '';
  return <div key={column.id} className={`${getWidthClass(column.width)} flex flex-col ${dividerClass}`}>
      {renderColumnChildren(column, ctx)}
    </div>;
}

/**
 * Row padding is a per-breakpoint arbitrary CSS value (e.g. { desktop: '10px' }),
 * not one of a fixed set of options — Tailwind's static class scanner can't
 * pick up a dynamically-built arbitrary-value class like `p-[${value}]` at
 * build time, so (same technique as BigNewsWithicon's mobile title-size
 * override) this renders a scoped <style> tag with real media queries
 * instead. Each breakpoint applies only what's explicitly configured — no
 * fallback/inheritance between breakpoints — matching the Builder's
 * per-breakpoint padding controls being independent of each other.
 */
function RowPaddingStyle(t0) {
  const $ = _c(7);
  const {
    rowId,
    padding
  } = t0;
  if (!padding || !padding.mobile && !padding.tablet && !padding.desktop) {
    return null;
  }
  const className = `row-padding-${rowId}`;
  let rules;
  if ($[0] !== className || $[1] !== padding.desktop || $[2] !== padding.mobile || $[3] !== padding.tablet) {
    rules = [];
    if (padding.mobile) {
      rules.push(`.${className}{padding:${padding.mobile};}`);
    }
    if (padding.tablet) {
      rules.push(`@media (min-width:768px){.${className}{padding:${padding.tablet};}}`);
    }
    if (padding.desktop) {
      rules.push(`@media (min-width:1024px){.${className}{padding:${padding.desktop};}}`);
    }
    $[0] = className;
    $[1] = padding.desktop;
    $[2] = padding.mobile;
    $[3] = padding.tablet;
    $[4] = rules;
  } else {
    rules = $[4];
  }
  const t1 = rules.join("");
  let t2;
  if ($[5] !== t1) {
    t2 = <style>{t1}</style>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function renderRow(row: Row, ctx: RenderContext): React.ReactNode {
  if (row.hidden) return null;
  const paddingClass = row.padding ? `row-padding-${row.id}` : '';
  const columns = row.columns || [];
  return <div key={row.id} className={`grid grid-cols-12 ${paddingClass}`}>
      <RowPaddingStyle rowId={row.id} padding={row.padding} />
      {/* A single divider between side-by-side columns (e.g. main article
          content | related-stories sidebar) — every column except the
          last one in the row gets a right border, so a 2-column row shows
          exactly one line between them regardless of column count. */}
      {columns.map((column, idx) => renderColumn(column, ctx, idx < columns.length - 1))}
    </div>;
}

/**
 * Walks an article-template's pageJson.sections -> rows -> columns -> components
 * tree, dispatching Article* component types to their dedicated renderers and
 * falling back to the generic page-builder MultiComponentRenderer for anything
 * else. Reuses the page-builder tree TYPES only (Section/Row/Column/PageComponent)
 * — not the generic collection-loop/pagination/tabBar/slider runtime, which
 * doesn't apply to a single fetched `story`.
 */
export default function ArticleTemplateRenderer(t0) {
  const $ = _c(10);
  const {
    story,
    template,
    showPublishedOn: t1,
    relatedStories: t2,
    videos: t3
  } = t0;
  const showPublishedOn = t1 === undefined ? true : t1;
  let t4;
  if ($[0] !== showPublishedOn || $[1] !== story || $[2] !== t2 || $[3] !== t3 || $[4] !== template.pageJson?.sections || $[5] !== template.sections || $[6] !== template.templateType) {
    const relatedStories = t2 === undefined ? [] : t2;
    const videos = t3 === undefined ? [] : t3;
    const sections = template.pageJson?.sections || template.sections || [];
    const ctx = {
      story,
      showPublishedOn,
      relatedStories,
      videos,
      templateType: template.templateType
    };
    let t5;
    if ($[8] !== story.slug) {
      t5 = <TaboolaLoader pageType="article" pageKey={story.slug} />;
      $[8] = story.slug;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = <>{t5}{sections.map(section => <div key={section.id}>{(section.rows || []).map(row => renderRow(row, ctx))}</div>)}</>;
    $[0] = showPublishedOn;
    $[1] = story;
    $[2] = t2;
    $[3] = t3;
    $[4] = template.pageJson?.sections;
    $[5] = template.sections;
    $[6] = template.templateType;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}